import { KeyboardEventHandler, useCallback, useMemo, useRef } from 'react';

import computeScrollIntoView from 'compute-scroll-into-view';
import {
  useCombobox,
  UseComboboxState,
  UseComboboxStateChange,
  UseComboboxStateChangeOptions,
  useMultipleSelection,
  UseMultipleSelectionProps,
} from 'downshift';

import { useForwardedRef } from '@adsk/alloy-react-helpers';

import useAutocomplete from './useAutocomplete';
import useDropdownId from './useDropdownId';
import useKeepMenuOpenOnBlur from './useKeepMenuOpenOnBlur';
import {
  checkReferenceEquality,
  defaultIsItemDisabled,
  defaultLabelGetter,
} from '../helpers';
import { flattenGroupItemsToDownshift } from '../helpers/groupedItems';
import {
  getMultipleSelectionHandlers,
  toggleItemSelection,
} from '../helpers/sharedMultipleSelection';
import {
  GenericItem,
  GetInputProps,
  ItemsOrGroupedItems,
  UseMultiAutocompleteProps,
} from '../types';

/**
 * A hook used to manage all logic needed for a multiple select Autocomplete (searchable) dropdown.
 */
const useMultiAutocomplete = <
  TItem extends GenericItem,
  TTarget extends HTMLElement = HTMLDivElement,
>({
  id,
  items,
  groupedItems,
  selectedItems: selectedItemsProp,
  itemToString = defaultLabelGetter,
  multipleSelectionStateReducer,
  stateReducer: stateReducerProp,
  target: targetRefProp,
  overlayRef: overlayRefProp,
  inputRef: inputRefProp,
  isItemDisabled = defaultIsItemDisabled,
  onStateChange: onStateChangeProp,
  ...props
}: UseMultiAutocompleteProps<TItem, TTarget>) => {
  const dropdownId = useDropdownId(id);
  const maybeGroupedItems = useMemo(
    () => items ?? groupedItems ?? [],
    [groupedItems, items],
  );
  checkReferenceEquality({
    items: maybeGroupedItems,
    ...props,
    selectedItems: selectedItemsProp,
  });

  const inputRef = useForwardedRef(inputRefProp ?? null);
  const targetRef = useForwardedRef(targetRefProp ?? null);
  const overlayRef = useForwardedRef(overlayRefProp ?? null);

  const useMultipleSelectionArgs: UseMultipleSelectionProps<TItem> = {};
  if (selectedItemsProp) {
    // Setting this to undefined results in "cannot get length of undefined", so do this hack
    useMultipleSelectionArgs.selectedItems = selectedItemsProp;
  }

  if (multipleSelectionStateReducer) {
    // Setting this to undefined results in "stateReducer is not a function"
    useMultipleSelectionArgs.stateReducer = multipleSelectionStateReducer;
  }

  const {
    getDropdownProps,
    addSelectedItem: rawAddSelectedItem,
    removeSelectedItem: rawRemoveSelectedItem,
    getSelectedItemProps: rawGetSelectedItemProps,
    selectedItems,
    ...useMultipleSelectionReturnValue
  } = useMultipleSelection<TItem>({
    ...useMultipleSelectionArgs,
    // Setting initial active index blocks downshift from focusing input on mount
    initialActiveIndex: 0,
    ...props,
  });

  const flatItems = useMemo(
    () => flattenGroupItemsToDownshift(maybeGroupedItems),
    [maybeGroupedItems],
  );

  const _isItemDisabled = useCallback(
    (item: TItem) => isItemDisabled(item, flatItems.indexOf(item)),
    [flatItems, isItemDisabled],
  );

  const itemWasRemoved = useRef(false);

  const addSelectedItem = useCallback(
    (item: TItem) => {
      if (_isItemDisabled(item)) {
        return;
      }

      rawAddSelectedItem(item);
    },
    [_isItemDisabled, rawAddSelectedItem],
  );

  const removeSelectedItem = useCallback(
    (item: TItem) => {
      if (_isItemDisabled(item)) {
        return;
      }

      itemWasRemoved.current = true;

      rawRemoveSelectedItem(item);
    },
    [_isItemDisabled, rawRemoveSelectedItem],
  );

  const { onKeyDown: controlOnKeyDown, getShouldKeepMenuOpenOnBlur } =
    useKeepMenuOpenOnBlur({ id: dropdownId });

  const autocompleteStateReducer = useCallback(
    (
      state: UseComboboxState<TItem>,
      { changes, type }: UseComboboxStateChangeOptions<TItem>,
    ): Partial<UseComboboxState<TItem>> => {
      if (type !== useCombobox.stateChangeTypes.InputBlur) {
        itemWasRemoved.current = false;
      }

      switch (type) {
        case useCombobox.stateChangeTypes.InputClick:
          if (state.isOpen) {
            // if the user clicks on the input while the menu is open, then we should keep the menu open with its highlighted index
            return {
              ...changes,
              highlightedIndex: state.highlightedIndex,
              isOpen: true,
            };
          }

          return changes;
        case useCombobox.stateChangeTypes.InputBlur: {
          const overrides: Partial<UseComboboxState<TItem>> = {
            isOpen: getShouldKeepMenuOpenOnBlur(),
            inputValue: state.inputValue, // keep user entered input value on blur
          };

          // NOTE(madeira): If the last operation was deselection, we want to force open state
          if (itemWasRemoved.current) {
            overrides.isOpen = true;
          }

          return {
            ...changes,
            ...overrides,
          };
        }
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
        case useCombobox.stateChangeTypes.FunctionCloseMenu:
        case useCombobox.stateChangeTypes.ToggleButtonClick:
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
          return {
            ...changes,
            inputValue: '', // remove user entered input value on selection or menu close
          };
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick: {
          const overrides: Partial<UseComboboxState<TItem>> = {
            isOpen: true, // keep menu open after selection
            highlightedIndex: state.highlightedIndex, // maintain highlighted item after selection
          };

          if (changes.selectedItem) {
            overrides.inputValue = ''; // reset user entered input value after selection

            if (items) {
              overrides.highlightedIndex = items.indexOf(changes.selectedItem);
            }
          }

          return {
            ...changes,
            ...overrides,
          };
        }
        default:
          return changes;
      }
    },
    [getShouldKeepMenuOpenOnBlur, items],
  );

  const onStateChange = useCallback(
    ({ type, selectedItem }: UseComboboxStateChange<TItem>) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick: {
          toggleItemSelection({
            item: selectedItem,
            selectedItems,
            addSelectedItem,
            removeSelectedItem,
          });
          break;
        }
        default:
          break;
      }

      onStateChangeProp?.({ type, selectedItem });
    },
    [addSelectedItem, onStateChangeProp, removeSelectedItem, selectedItems],
  );

  const stateReducer = useCallback(
    (
      state: UseComboboxState<TItem>,
      actionAndChanges: UseComboboxStateChangeOptions<TItem>,
    ) => {
      const updatedState = autocompleteStateReducer(state, actionAndChanges);
      const customUpdatedState = stateReducerProp?.(state, actionAndChanges);
      return {
        ...actionAndChanges.changes,
        ...updatedState,
        ...customUpdatedState,
      };
    },
    [autocompleteStateReducer, stateReducerProp],
  );

  const centerScrollIntoView = useCallback(
    (node: HTMLElement, menuNode: HTMLElement) => {
      if (!node || !menuNode) {
        return;
      }

      const actions = computeScrollIntoView(node, {
        boundary: menuNode,
        block: 'center',
        scrollMode: 'if-needed',
      });
      actions.forEach(({ el, top, left }) => {
        el.scrollTop = top;
        el.scrollLeft = left;
      });
    },
    [],
  );

  // workaround, the implicit XOR type gets lost causing type errors.  Casting it here makes it explicit
  const itemArgs = { items, groupedItems } as ItemsOrGroupedItems<TItem>;

  const {
    controlProps: {
      isOpen,
      getInputProps: rawGetInputProps,
      selectedItem: _ignored1,
      ...controlProps
    },
    menuProps: {
      getItemProps: rawGetItemProps,
      selectedItem: _ignored2,
      ...menuProps
    },
    ...useAutocompleteReturnValue
  } = useAutocomplete({
    id: dropdownId,
    ...itemArgs,
    itemToString,
    selectedItem: null,
    inputRef,
    target: targetRef,
    overlayRef,
    stateReducer,
    onStateChange,
    isItemDisabled,
    scrollIntoView: centerScrollIntoView,
    ...props,
  });

  const getInputProps = useCallback<GetInputProps>(
    ({ onKeyDown: onKeyDownProp, ...options } = {}) => {
      const dropdownProps = getDropdownProps({
        preventKeyAction: isOpen,
      });

      const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
        controlOnKeyDown(e);

        onKeyDownProp?.(e);
      };

      return rawGetInputProps({
        ...dropdownProps,
        onKeyDown,
        ...options,
      });
    },
    [controlOnKeyDown, getDropdownProps, isOpen, rawGetInputProps],
  );

  const multipleSelectionHandlers = useMemo(
    () =>
      getMultipleSelectionHandlers({
        id: dropdownId,
        selectedItems,
        setSelectedItems: useMultipleSelectionReturnValue.setSelectedItems,
        isItemDisabled: _isItemDisabled,
        closeMenu: useAutocompleteReturnValue.closeMenu,
        rawGetItemProps,
        rawGetSelectedItemProps,
        refToFocusOnClear: inputRef,
      }),
    [
      dropdownId,
      inputRef,
      _isItemDisabled,
      rawGetItemProps,
      rawGetSelectedItemProps,
      selectedItems,
      useAutocompleteReturnValue.closeMenu,
      useMultipleSelectionReturnValue.setSelectedItems,
    ],
  );

  return {
    controlProps: {
      ...controlProps,
      isOpen,
      getInputProps,
      selectedItems,
      removeSelectedItem,
      ...multipleSelectionHandlers.controlProps,
    },
    menuProps: {
      ...menuProps,
      selectedItems,
      addSelectedItem,
      removeSelectedItem,
      ...multipleSelectionHandlers.menuProps,
    },
    ...useMultipleSelectionReturnValue,
    ...useAutocompleteReturnValue,
  };
};

export default Object.assign(useMultiAutocomplete, {
  stateChangeTypes: useCombobox.stateChangeTypes,
  multipleSelectionStateChangeTypes: useMultipleSelection.stateChangeTypes,
});
